import axios from 'axios'
import { getCartoData } from './utilsCartoApi'
import {replaceUnderscoresWithSpaces} from '../utils'
import config from '../content/config'

import {
	FID_COLUMN,
	GEO_ID_ACCESSOR,
	STATE_NAME_ACCESSOR,
	COUNTY_LAT_LON_FIELDS,
	ACS_DATA_NAME_ACCESSOR,
	ALL_RURAL_DEFINITIONS_LAYER,
	GEOGRAPHY_OPTIONS,
	ACS_DATA_NAME_ACCESSOR_IN_TRACT_DATA,
	LOOKUP_TABLE_FIELDS,
	BLOCK_FIPS_TO_GEO_ID_LOOKUP_TABLE, BLOCKS_FIPS_FIELD, MISC_ATTR_FIELDS
} from "../constants"
import {
	getLayerSpecificFields, groupArrayIntoChunks,
	isACensusTract, isAttrLayer
} from "../utilsData";
import RURAL_DEFINITION from '../content/rural-definition/rural-definition'
import {getMinMaxQueryForFields} from "./utilsSQL";


// Class that handles most logic for Carto stats (calculations, display, colors, etc)
export default class StatsManager {
	constructor({apiKey, sqlFilterToApplyToAllACSLayers, ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS, ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS}) {
		this._apiKey = apiKey
		this._sqlFilterToApplyToAllACSLayers = sqlFilterToApplyToAllACSLayers
		this._metadata = []
		
		this.ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS = ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS // there's probably a more OO way of doing this...
		this.ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS = ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS // there's probably a more OO way of doing this...
	}

	// =======================================
	// API request functions
	// =======================================

	// Using the FCC API (from lat-lon to county) https://geo.fcc.gov/api/census/
	// Example: https://geo.fcc.gov/api/census/block/find?latitude=41.11&longitude=-83.16&format=json
	getCensusBlockFIPSCodeFromLatLon = ({lat, lon, fullLocation}, onComplete) => {
		axios.get('https://geo.fcc.gov//api/census/block/find', {
			params: {latitude: lat, longitude: lon, format: 'json', fullLocation}
		})
		.then((response) => {
			const censusBlockFIPSCode = response.data.Block.FIPS
			onComplete(censusBlockFIPSCode)
		})
		.catch((error) => {
			console.log(error)
		})
	}

	getCartoAttributeDataPromiseFor = (stateName, cartoLayerId) => {
		const ACS_CATEGORICAL_FIELDS = ['st_name', /*'FIELD_TO_TEST_ERROR_HANDLING'*/]
		
		const {lat, lon} = COUNTY_LAT_LON_FIELDS
		const NUMERIC_AND_CATEGORICAL_FIELDS = [
			...this.getAllACSNumericFieldNames(),
			...ACS_CATEGORICAL_FIELDS,
		].join(',')
		const RURAL_DEFINITION_FIELDS = Object.values(RURAL_DEFINITION).map(ruralDefinitionObj => ruralDefinitionObj.acsDataField).join(',')
		const _MISC_ATTR_FIELDS = [...Object.values(MISC_ATTR_FIELDS), ...getLayerSpecificFields(cartoLayerId)].join(',')
		const _isACensusTract = isACensusTract(cartoLayerId)
		const fields = (_isACensusTract ? `${ACS_DATA_NAME_ACCESSOR_IN_TRACT_DATA},` : '') + `${ACS_DATA_NAME_ACCESSOR},${FID_COLUMN},${NUMERIC_AND_CATEGORICAL_FIELDS},${lat},${lon},${GEO_ID_ACCESSOR},${RURAL_DEFINITION_FIELDS},${_MISC_ATTR_FIELDS}`
		const sqlFilterToApplyToAllACSLayersFormatted = this._sqlFilterToApplyToAllACSLayers && `AND ${this._sqlFilterToApplyToAllACSLayers}`
		const countyQuery = [{query: `SELECT ${fields} FROM "${config.CARTO_USER}".${cartoLayerId} WHERE st_name = '${stateName}' ${sqlFilterToApplyToAllACSLayersFormatted}`}]
		return getCartoData(countyQuery, this._apiKey)
	}

	getCartoSummaryDataForVis = (cartoLayerId) => {
		const st_numericFields = Object.values(this.ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS).map(f => 'st_' + f).join(', ')
		const us_numericFields = Object.values(this.ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS).map(f => 'us_' + f).join(', ')
		const fields = `${st_numericFields}, ${us_numericFields}, ${STATE_NAME_ACCESSOR}`
		const query = [{query: `SELECT DISTINCT ON (${STATE_NAME_ACCESSOR}) ${fields} FROM "${config.CARTO_USER}".${cartoLayerId}`}]
		return getCartoData(query, this._apiKey)
	}
	
	getCartoGeoIdLookupTablePromiseFor = (censusBlockFIPSCode) => {
		const fields = Object.values(LOOKUP_TABLE_FIELDS).join(',')
		const query = [{query: `SELECT ${fields} FROM "${config.CARTO_USER}".${BLOCK_FIPS_TO_GEO_ID_LOOKUP_TABLE} WHERE ${BLOCKS_FIPS_FIELD} = '${censusBlockFIPSCode}'`}]
		return getCartoData(query, this._apiKey)
	}
	
	getUSASpendingDataPromiseFor = (countyFIPSCode = '50011') => {
		const USA_SPENDING_DATA_FIELDS = ['ppp_geoid_co', 'ppp_st_co', 'awarding_agency_name',  'awarding_sub_agency_name', 'total_funding_amount', 'usaspending_link', 'rec_name', 'rec_st_co', 'cfda_number', 'award_description', 'business_types_short', ]
		const fields = Object.values(USA_SPENDING_DATA_FIELDS).join(',')
		const USA_SPENDING_DATA_TABLE = 'usaspending_popup_national'
		const USA_SPENDING_PPP_COUNTY_FIELD = 'ppp_geoid_co'
		// So, these queries could be HUUUUGE (eg a VT county had 3k+) so I put a safety limit of 5k. A long term solution needs to be paginated fetching.
		const query = [{query: `SELECT ${fields} FROM "${config.CARTO_USER}".${USA_SPENDING_DATA_TABLE} WHERE ${USA_SPENDING_PPP_COUNTY_FIELD} = '${countyFIPSCode}' LIMIT 5000`}]
		return getCartoData(query, this._apiKey)
	}
	
	getAllACSNumericFieldNames = () => Object.keys(this.getAllACSNumericFieldInLookupTable())
	getAllACSNumericFieldInLookupTable = () => ({...this.ACS_NUMERIC_FIELDS_WITH_US_AND_STATE_FIELDS, ...this.ACS_NUMERIC_FIELDS_WITHOUT_US_AND_STATE_FIELDS})
	

	getCartoAttributeDataExtents = (cartoLayerId, selectedUSStatesToFilterWithin) => {
		/*
		SELECT
			MIN(avg_commute_in_minutes_2017) as min_avg_commute_in_minutes_2017,
			MAX(avg_commute_in_minutes_2017) as max_avg_commute_in_minutes_2017
		FROM "cori-org".attr_tract
		*/
		
		const chunkedACSNumericFields = groupArrayIntoChunks(this.getAllACSNumericFieldNames(), 10)
		const queries = chunkedACSNumericFields.reduce((_queries, currentChunkOfFields) => [..._queries, getMinMaxQueryForFields(currentChunkOfFields, cartoLayerId, selectedUSStatesToFilterWithin)], [])
		
		return getCartoData(queries, this._apiKey)
	}

	formatFieldExtentsFromCartQuery = (res) => {
		const maxAndMinForEachField = res.reduce((_maxAndMinForEachField, {data}) => ({..._maxAndMinForEachField, ...data.rows[0]}), {})
		
		return this.getAllACSNumericFieldNames().reduce((_fieldExtentsCache, fieldName) => {
			const min = Math.floor(maxAndMinForEachField[`min_${fieldName}`])
			const max = Math.ceil(maxAndMinForEachField[`max_${fieldName}`])
			return {
				..._fieldExtentsCache,
				[fieldName]: [min, max],
			}
		}, {})
	}

	formatMetadata = ([response]) => {
		this._metadata = response.data.rows.reduce(
			(metadata, metadataRow) => ({
				...metadata,
				[this._getMetadataKey(metadataRow.postgres_name, metadataRow.cori_field_name)]: metadataRow,
			})
			, {})
	}

	loadMetadata = () => {
		// Currently it seems like only RUral definitions/tract is using the metadata (???)
		const isARuralDefinitionOrTract = "postgres_name LIKE '%rural_definitions_all%' OR postgres_name LIKE '%attr_tract%'"
		const query = [{query: `SELECT cori_field_name, display_field_name,field_description, postgres_name FROM "${config.CARTO_USER}".metadata_field WHERE ${isARuralDefinitionOrTract}`}]
		getCartoData(query, this._apiKey).then((res) => this.formatMetadata(res))

	}

	// =======================================
	// Metadata
	// =======================================
	_getMetadataKey = (cartoLayerId, fieldName) => `${cartoLayerId}, ${fieldName}`

	_getMetaDataRow = (cartoLayerId, fieldName) => this._metadata[this._getMetadataKey(cartoLayerId, fieldName)] || {}

	getDisplayName = (cartoLayerId, fieldName) => {
		const metadataRow = this._getMetaDataRow(cartoLayerId, fieldName)
		return metadataRow.display_field_name || replaceUnderscoresWithSpaces(fieldName)
	}

	getFieldDescription = (cartoLayerId, fieldName) => {
		const metadataRow = this._getMetaDataRow(
			isAttrLayer(cartoLayerId) ? GEOGRAPHY_OPTIONS.tract.cartoLayerId : cartoLayerId,
			fieldName
		)
		return metadataRow.field_description || ''
	}

	getRuralDefinitionInfo = (ruralCartoLayerId) => {
		const layer = ALL_RURAL_DEFINITIONS_LAYER
		const fieldName = ruralCartoLayerId.replace('_by_state', '')

		const ruralDefinitionDescription = this.getFieldDescription(layer, fieldName)
		
		const {
			nameLong,
			nameShort,
			organizationLong,
			organizationShort,
		} = RURAL_DEFINITION[ruralCartoLayerId]
		
		const summary = ruralDefinitionDescription.split('\n')[0]
		const [detailedExplanation, moreInfoURL] = ruralDefinitionDescription.split('link: ')
		return {
			nameLong,
			nameShort,
			organizationLong,
			organizationShort,
			
			summary,
			detailedExplanation,
			moreInfoURL,
		}
	}
}
